import { useMemo } from 'react';
import {
  Building2,
  Factory,
  Hotel,
  Leaf,
  Stethoscope,
  Store,
  Truck,
  University,
  Warehouse,
  type LucideIcon,
} from 'lucide-react';
import { Container } from '../layout/Container';
import { Reveal } from '../motion/Reveal';
import { cn } from '../../lib/cn';

interface Sector {
  slug: string;
  /** Page réelle la plus pertinente (les routes /secteurs/* sont dépréciées). */
  href: string;
  label: string;
  metric: string;
  icon: LucideIcon;
  tone: 'brand' | 'accent' | 'lavender' | 'sage';
}

const SECTORS: Sector[] = [
  { slug: 'tertiaire', href: '/tertiaire', label: 'Tertiaire', metric: 'Primes énergie, obligation 2030', icon: Building2, tone: 'brand' },
  { slug: 'industrie', href: '/industrie', label: 'Industrie', metric: 'Primes énergie, audit', icon: Factory, tone: 'accent' },
  { slug: 'logistique', href: '/tertiaire/travaux', label: 'Logistique', metric: 'Éclairage, pilotage', icon: Warehouse, tone: 'lavender' },
  { slug: 'retail', href: '/tertiaire/travaux/relamping-led', label: 'Retail / GMS', metric: 'Froid, éclairage', icon: Store, tone: 'brand' },
  { slug: 'hotellerie', href: '/tertiaire/travaux/pac-tertiaire', label: 'Hôtellerie', metric: 'Pompe à chaleur, eau chaude', icon: Hotel, tone: 'accent' },
  { slug: 'sante', href: '/tertiaire/travaux/gtb', label: 'Santé', metric: 'Chauffage, pilotage', icon: Stethoscope, tone: 'lavender' },
  { slug: 'agriculture', href: '/agriculteurs', label: 'Agriculture', metric: 'Primes énergie agricoles', icon: Leaf, tone: 'accent' },
  { slug: 'transport', href: '/cee/fiches', label: 'Transport', metric: 'Primes énergie transport', icon: Truck, tone: 'sage' },
  { slug: 'collectivites', href: '/collectivites', label: 'Collectivités', metric: 'Fonds vert, primes énergie', icon: University, tone: 'brand' },
];

const TONE_BG = {
  brand: 'bg-brand-50 text-brand-700 ring-brand-200/60',
  accent: 'bg-accent-50 text-accent-800 ring-accent-200/60',
  lavender: 'bg-lavender-50 text-lavender-700 ring-lavender-200/60',
  sage: 'bg-sage-100 text-sage-500 ring-sage-200/60',
} as const;

interface SectorPillProps {
  sector: Sector;
  /** false → lien retiré du tab-order (copies décoratives du marquee). */
  tabbable?: boolean;
}

function SectorPill({ sector, tabbable = true }: SectorPillProps) {
  const Icon = sector.icon;
  return (
    <a
      href={sector.href}
      tabIndex={tabbable ? undefined : -1}
      className="group/pill inline-flex shrink-0 items-center gap-2.5 rounded-full border border-stone-200/70 bg-paper py-2 pl-2 pr-4 shadow-[0_1px_2px_rgba(10,10,10,.04)] transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-elevated"
    >
      <span
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-1 ring-inset transition-transform duration-200 ease-out group-hover/pill:scale-105',
          TONE_BG[sector.tone],
        )}
      >
        <Icon className="h-3.5 w-3.5" strokeWidth={1.8} />
      </span>
      <span className="flex flex-col">
        <span className="text-[12.5px] font-semibold leading-tight text-ink">{sector.label}</span>
        <span className="font-mono text-xs font-medium leading-tight text-stone-500">
          {sector.metric}
        </span>
      </span>
    </a>
  );
}

interface RowProps {
  reverse?: boolean;
  duration: number;
  items: Sector[];
  /**
   * true → rangée purement visuelle (répétition des mêmes liens) :
   * masquée des lecteurs d'écran et retirée du tab-order.
   */
  decorative?: boolean;
}

function MarqueeRow({ reverse = false, duration, items, decorative = false }: RowProps) {
  return (
    <div
      aria-hidden={decorative || undefined}
      className="group/row flex w-full overflow-hidden mask-fade-edges"
    >
      {[0, 1].map((dup) => (
        <ul
          key={dup}
          aria-hidden={!decorative && dup === 1 ? true : undefined}
          className={cn(
            'flex shrink-0 items-center gap-3 pr-3',
            'group-hover/row:[animation-play-state:paused]',
          )}
          style={{
            animation: `marquee ${duration}s linear infinite`,
            animationDirection: reverse ? 'reverse' : 'normal',
          }}
        >
          {items.map((s) => (
            <li key={`${dup}-${s.slug}`}>
              {/* Seule la 1re copie de la rangée principale reste focusable. */}
              <SectorPill sector={s} tabbable={!decorative && dup === 0} />
            </li>
          ))}
        </ul>
      ))}
    </div>
  );
}

export function SectorsCarousel() {
  const row1 = useMemo(() => SECTORS, []);
  const row2 = useMemo(() => [...SECTORS].reverse(), []);

  return (
    <section
      aria-labelledby="sectors-carousel-title"
      className="relative bg-paper py-14 md:py-20"
    >
      <Container>
        <Reveal>
          <div className="mx-auto mb-10 max-w-2xl text-center">
            <p className="eyebrow justify-center">Secteurs maîtrisés</p>
            <h2
              id="sectors-carousel-title"
              className="mt-3 font-display text-2xl font-bold tracking-tight text-balance text-ink md:text-3xl"
            >
              Votre secteur a ses <span className="text-gradient">leviers d&rsquo;économie propres</span>. On les connaît.
            </h2>
            <p className="mt-3 text-[14.5px] leading-relaxed text-stone-600">
              Tertiaire, industrie, logistique, hôtellerie, santé, agriculture… Chaque secteur a
              ses primes CEE spécifiques et ses principaux postes d'économies d'énergie.
            </p>
          </div>
        </Reveal>
      </Container>

      <Reveal delay={0.05}>
        <div className="space-y-3">
          <MarqueeRow items={row1} duration={48} />
          <MarqueeRow items={row2} reverse duration={56} decorative />
        </div>
      </Reveal>

      <Container className="mt-8 text-center">
        <a
          href="/cee/fiches"
          className="group/lnk inline-flex items-center gap-1 text-[12.5px] font-semibold text-brand-700 underline-offset-2 hover:underline"
        >
          Voir les primes par secteur
          <span aria-hidden className="transition-transform duration-200 ease-out group-hover/lnk:translate-x-0.5">→</span>
        </a>
      </Container>
    </section>
  );
}
